import { useEffect, useRef, useState } from "react";
import { useQuery } from "convex/react";
import { Loader2 } from "@/shared/ui/icons";

import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { api as cyranoApi } from "@/core/api/client";
import { useSession } from "./useSession";
import { AgencyStep } from "./onboarding/AgencyStep";
import { DoneStep } from "./onboarding/DoneStep";
import { LanguageStep, hasUserChosenLocale } from "./onboarding/LanguageStep";
import { OnboardingChrome } from "./onboarding/OnboardingChrome";

/**
 * Wizard 3 étapes affiché APRÈS bootstrap pour un user direct-signup
 * (0 memberships) OU pour un user qui a créé son agence mais n'a pas
 * terminé l'onboarding (accounts[0].onboardingCompletedAt absent).
 *
 *   Étape 1 — Agence : nom + logo → crée WorkOS Org + Convex account
 *   Étape 2 — Done   : récap + bouton "Créer mon premier créateur"
 *
 * Dropp.fans N'EST PLUS demandé pendant l'onboarding (refonte 2026-05-30).
 * La connexion Dropp se fait au niveau organisation (onglet Settings →
 * Méthodes de paiement) et n'est plus un prérequis à la création d'un
 * créateur (usage CRM-only possible). Les 20% (commission) ne gatent que
 * l'agent Cyrano (offre de contenu payant), côté backend.
 *
 * À la sortie, on patch `accounts.onboardingCompletedAt` → OrgGate route
 * vers le cockpit shell qui détecte `creators.length === 0` et force le
 * workspace single-panel "Creators".
 *
 * Les users invités ne voient JAMAIS cet écran : ils arrivent avec une
 * membership pré-créée + un `onboardingCompletedAt` déjà set sur l'account
 * de leur agence (donc accounts.length > 0 ET onboardingCompletedAt présent).
 */
type Step = "agency" | "done";

export function OnboardingScreen() {
  const { user, signOut } = useSession();
  const state = useQuery(api.onboarding.getMyOnboardingState);
  const accounts = useQuery(api.accounts.listMine);

  // Pré-écran : si l'user n'a jamais explicitement choisi sa langue, on
  // l'affiche AVANT le wizard pour qu'il ne bloque pas sur un onboarding
  // dans une langue qu'il ne lit pas. Une fois choisi, le flag localStorage
  // évite de redemander à chaque session.
  const [needsLocale, setNeedsLocale] = useState<boolean>(() => !hasUserChosenLocale());

  // Mode UI courant. Calculé depuis le state Convex au mount, puis géré
  // localement (on n'écrase pas quand la query rerun, sinon l'user revient
  // en arrière à chaque sync).
  const [step, setStep] = useState<Step | null>(null);

  // Auto-recovery : si le user a un account mais sa session Cyrano n'a pas
  // d'`org_id` (cas legacy : Org créée AVANT le fix select-organization de
  // 2026-05-13), on bind automatiquement la session à l'org du 1er account
  // puis on reload pour que useSession voie le nouvel org_id.
  // Mécanisme one-shot via useRef pour ne pas boucler.
  const autoRecoveryFiredRef = useRef(false);
  useEffect(() => {
    if (autoRecoveryFiredRef.current) return;
    if (!user || !accounts || accounts.length === 0) return;
    if (user.workosOrgId) return; // déjà bound, no-op
    const orgId = accounts[0]?.workosOrgId;
    if (!orgId) return;
    autoRecoveryFiredRef.current = true;
    (async () => {
      try {
        await cyranoApi.post("/api/auth/select-organization", { org_id: orgId });
        // Reload : useSession refetche /api/auth/me qui retourne maintenant
        // workos_org_id correct → middleware /api/* accepte les calls suivants.
        window.location.reload();
      } catch (err) {
        console.error("OnboardingScreen: auto-recovery select-organization failed:", err);
      }
    })();
  }, [user, accounts]);

  // Init du step à partir du state Convex — une seule fois quand la query
  // résout. Après ça, c'est le user qui pilote via onComplete des sub-steps.
  useEffect(() => {
    if (!state || step !== null) return;
    if (!state.hasAccount) {
      setStep("agency");
      return;
    }
    setStep("done");
  }, [state, step]);

  // Pré-écran langue : court-circuite tout le reste tant que l'user n'a pas
  // choisi. On l'affiche AVANT le loader Convex pour éviter le flash de
  // l'écran d'agence dans la mauvaise langue.
  if (needsLocale) {
    return <LanguageStep onChosen={() => setNeedsLocale(false)} />;
  }

  if (state === undefined || step === null) {
    return (
      <div className="flex h-full items-center justify-center bg-canvas">
        <Loader2 className="h-6 w-6 animate-spin text-faint" />
      </div>
    );
  }

  return (
    <OnboardingChrome
      step={step}
      onSignOut={() => void signOut()}
    >
      {step === "agency" && (
        <AgencyStep
          onCompleted={() => setStep("done")}
        />
      )}
      {step === "done" && state.hasAccount && state.accountId && (
        <DoneStep
          accountId={state.accountId as Id<"accounts">}
        />
      )}
    </OnboardingChrome>
  );
}
